import { LogLevel, LoggerConfig, LogMode } from './types';
import {
  DevFileTransport,
  ApplicationInsightsTransport,
  ConsoleTransport,
} from './transports';

export class LoggingConfig {
  private static getLogMode(): LogMode {
    const mode = import.meta.env.VITE_LOG_MODE || import.meta.env.MODE;

    switch (mode) {
      case 'debug':
      case 'test':
        return LogMode.DEBUG;
      case 'development':
        return LogMode.DEVELOPMENT;
      case 'production':
        return LogMode.PRODUCTION;
      default:
        return import.meta.env.PROD ? LogMode.PRODUCTION : LogMode.DEVELOPMENT;
    }
  }

  private static getSamplingRate(): number {
    const envSampling = import.meta.env.VITE_LOG_SAMPLING_RATE;
    if (envSampling) {
      return parseInt(envSampling, 10);
    }

    const mode = this.getLogMode();
    switch (mode) {
      case LogMode.DEBUG:
        return 1; // Log everything
      case LogMode.DEVELOPMENT:
        return 10; // Log 1 in 10
      case LogMode.PRODUCTION:
        return 1000; // Log 1 in 1000
      default:
        return 100;
    }
  }

  private static getLogLevel(): LogLevel {
    const envLevel = import.meta.env.VITE_LOG_LEVEL;
    if (envLevel && ['debug', 'info', 'warn', 'error'].includes(envLevel)) {
      return envLevel as LogLevel;
    }

    const mode = this.getLogMode();
    switch (mode) {
      case LogMode.DEBUG:
        return 'debug';
      case LogMode.DEVELOPMENT:
        return 'info';
      case LogMode.PRODUCTION:
        return 'warn';
      default:
        return 'info';
    }
  }

  private static createTransports(): any[] {
    const mode = this.getLogMode();
    const transports = [];

    switch (mode) {
      case LogMode.DEBUG:
        transports.push(new ConsoleTransport());
        transports.push(new DevFileTransport());
        break;

      case LogMode.DEVELOPMENT:
        transports.push(new DevFileTransport());
        // Add console transport if explicitly enabled
        if (import.meta.env.VITE_LOG_CONSOLE === 'true') {
          transports.push(new ConsoleTransport());
        }
        break;

      case LogMode.PRODUCTION:
        transports.push(new ApplicationInsightsTransport());
        break;
    }

    return transports;
  }

  public static createConfig(): LoggerConfig {
    return {
      level: this.getLogLevel(),
      transports: this.createTransports(),
      sampling: this.getSamplingRate(),
      module: 'spa-ui',
    };
  }

  public static getDebugInfo() {
    return {
      mode: this.getLogMode(),
      level: this.getLogLevel(),
      sampling: this.getSamplingRate(),
      env: {
        NODE_ENV: import.meta.env.MODE,
        VITE_LOG_MODE: import.meta.env.VITE_LOG_MODE,
        VITE_LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL,
        VITE_LOG_SAMPLING_RATE: import.meta.env.VITE_LOG_SAMPLING_RATE,
        VITE_LOG_CONSOLE: import.meta.env.VITE_LOG_CONSOLE,
        VITE_APPLICATIONINSIGHTS_CONNECTION_STRING: import.meta.env
          .VITE_APPLICATIONINSIGHTS_CONNECTION_STRING
          ? '[SET]'
          : '[NOT SET]',
      },
    };
  }
}
